// Tooltip moderno para todo Focus.
//
// Wrapper sobre Radix Tooltip con estilos propios del design system.
// Diseño dark, tipografía sólida, sombra suave, animación fade+scale,
// arrow visible. Apropiado para un CRM premium.
//
// Uso típico:
//   <Tooltip content="No pertenece a ningún tenant. Library del sistema...">
//     <span className={styles.globalChip}>Global</span>
//   </Tooltip>
//
// Para tooltips con contenido rich (multiline, links, formato), pasar JSX
// como content.
//
// Default side: top con auto-flip si no cabe.

import * as RadixTooltip from '@radix-ui/react-tooltip';
import type { ReactNode } from 'react';
import styles from './Tooltip.module.css';

interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  /** Lado donde aparece. Auto-flip si no cabe. */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Alineación contra el trigger. */
  align?: 'start' | 'center' | 'end';
  /** Delay en ms antes de mostrar (default 300ms — responsive sin intrusivo). */
  delayMs?: number;
  /** Si false, no se renderiza el tooltip (útil para condicionales). */
  enabled?: boolean;
}

export function Tooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  delayMs = 300,
  enabled = true,
}: TooltipProps) {
  if (!enabled) return <>{children}</>;

  return (
    <RadixTooltip.Provider delayDuration={delayMs} skipDelayDuration={100}>
      <RadixTooltip.Root>
        <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            side={side}
            align={align}
            sideOffset={6}
            collisionPadding={8}
            className={styles.content}
          >
            {content}
            <RadixTooltip.Arrow className={styles.arrow} width={10} height={5} />
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}
